// date pickers (MUI)
import {
  enUS as enUSDate,
  esES as esESDate,
  ruRU as ruRUDate,
  ptBR as ptBRDate,
  itIT as itITDate,
  frFR as frFRDate,
  deDE as deDEDate,
  elGR as elGRDate,
  viVN as viVNDate,
  zhCN as zhCNDate,
  jaJP as jaJPDate,
} from '@mui/x-date-pickers/locales';
// core (MUI)
import {
  enUS as enUSCore,
  esES as esESCore,
  ruRU as ruRUCore,
  ptBR as ptBRCore,
  itIT as itITCore,
  frFR as frFRCore,
  deDE as deDECore,
  elGR as elGRCore,
  viVN as viVNCore,
  zhCN as zhCNCore,
  jaJP as jaJPCore,
  arSA as arSACore,
} from '@mui/material/locale';
// data grid (MUI)
import {
  enUS as enUSDataGrid,
  esES as esESDataGrid,
  ruRU as ruRUDataGrid,
  ptBR as ptBRDataGrid,
  itIT as itITDataGrid,
  frFR as frFRDataGrid,
  deDE as deDEDataGrid,
  elGR as elGRDataGrid,
  viVN as viVNDataGrid,
  zhCN as zhCNDataGrid,
  jaJP as jaJPDataGrid,
  arSD as arSDDataGrid,
} from '@mui/x-data-grid/locales';

/**
 * Country code:
 * @see https://flagcdn.com/en/codes.json
 *
 * Number format code:
 * @see https://gist.github.com/raushankrjha/d1c7e35cf87e69aa8b4208a8171a8416
 */
export const allLangs = [
  {
    code: 'US',
    name: 'English',
    countryCode: 'US',
    adapterLocale: 'en',
    countryName: 'United States',
    numberFormat: { code: 'en-US', currency: 'USD' },
    systemValue: {
      components: { ...enUSCore.components, ...enUSDate.components, ...enUSDataGrid.components },
    },
  },
  {
    code: 'ES',
    name: 'Español',
    countryCode: 'ES',
    adapterLocale: 'es',
    countryName: 'Spain',
    numberFormat: { code: 'es-ES', currency: 'EUR' },
    systemValue: {
      components: { ...esESCore.components, ...esESDate.components, ...esESDataGrid.components },
    },
  },
  {
    code: 'PT',
    name: 'Português',
    countryCode: 'PT',
    adapterLocale: 'pt',
    countryName: 'Portugal',
    numberFormat: { code: 'pt-PT', currency: 'EUR' },
    systemValue: {
      components: { ...ptBRCore.components, ...ptBRDate.components, ...ptBRDataGrid.components },
    },
  },
  {
    code: 'FR',
    name: 'French',
    countryCode: 'FR',
    adapterLocale: 'fr',
    countryName: 'France',
    numberFormat: { code: 'fr-Fr', currency: 'EUR' },
    systemValue: {
      components: { ...frFRCore.components, ...frFRDate.components, ...frFRDataGrid.components },
    },
  },
  {
    code: 'IT',
    name: 'Italiano',
    countryCode: 'IT',
    adapterLocale: 'it',
    countryName: 'Italy',
    numberFormat: { code: 'it-IT', currency: 'EUR' },
    systemValue: {
      components: { ...itITCore.components, ...itITDate.components, ...itITDataGrid.components },
    },
  },
  {
    code: 'DE',
    name: 'Deutsch',
    countryCode: 'DE',
    adapterLocale: 'de',
    countryName: 'Germany',
    numberFormat: { code: 'de-DE', currency: 'EUR' },
    systemValue: {
      components: { ...deDECore.components, ...deDEDate.components, ...deDEDataGrid.components },
    },
  },
  {
    code: 'GR',
    name: 'Ελληνικά',
    countryCode: 'GR',
    adapterLocale: 'el',
    countryName: 'Greece',
    numberFormat: { code: 'el-GR', currency: 'EUR' },
    systemValue: {
      components: { ...elGRCore.components, ...elGRDate.components, ...elGRDataGrid.components },
    },
  },
  {
    code: 'VN',
    name: 'Tiếng Việt',
    countryCode: 'VN',
    adapterLocale: 'vi',
    countryName: 'Vietnam',
    numberFormat: { code: 'vi-VN', currency: 'VND' },
    systemValue: {
      components: { ...viVNCore.components, ...viVNDate.components, ...viVNDataGrid.components },
    },
  },
  {
    code: 'RU',
    name: 'Русский',
    countryCode: 'RU',
    adapterLocale: 'ru',
    countryName: 'Russia',
    numberFormat: { code: 'ru-RU', currency: 'RUB' },
    systemValue: {
      components: { ...ruRUCore.components, ...ruRUDate.components, ...ruRUDataGrid.components },
    },
  },
  {
    code: 'CN',
    name: '中國人',
    countryCode: 'CN',
    adapterLocale: 'zh-cn',
    countryName: 'China',
    numberFormat: { code: 'zh-CN', currency: 'CNY' },
    systemValue: {
      components: { ...zhCNCore.components, ...zhCNDate.components, ...zhCNDataGrid.components },
    },
  },
  {
    code: 'SA',
    name: 'عربي',
    countryCode: 'SA',
    adapterLocale: 'ar-sa',
    countryName: 'Saudi Arabia',
    numberFormat: { code: 'ar-SA', currency: 'AED' },
    systemValue: {
      components: { ...arSACore.components, ...arSDDataGrid.components },
    },
  },
  {
    code: 'JP',
    name: '日本語',
    countryCode: 'JP',
    adapterLocale: 'ja',
    countryName: 'Japan',
    numberFormat: { code: 'ja-JP', currency: 'JPY' },
    systemValue: {
      components: { ...jaJPCore.components, ...jaJPDate.components, ...jaJPDataGrid.components },
    },
  },
];

const languageCache = {};

export function getLanguageData(inputValue, fallbackLang) {
  if (!languageCache[inputValue]) {
    languageCache[inputValue] = allLangs.find(
      ({ code, name, adapterLocale }) =>
        [code, name, adapterLocale].some((field) =>
          field?.toLowerCase().includes(inputValue?.toLowerCase())
        ) ||
        (fallbackLang && adapterLocale === fallbackLang)
    ) ?? { name: '', code: '', adapterLocale: '' };
  }
  return languageCache[inputValue];
}
